import { Helmet } from "react-helmet";
import styled from "styled-components";
import React, { useState, useEffect } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Col, Row, Form, Spin, Input, Table } from "antd";
import { Empty, Select, Button, Typography, ConfigProvider } from "antd";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { StringParam, NumberParam, withDefault, useQueryParams } from "use-query-params";

import Role from "components/Role";
import EditableCell from "components/EditableCell";
import { ApiErrorTableComponent } from "components/ErrorHandler";

import { useAppDispatch } from "app/hooks";
import { openNotificationWithIcon } from "utils";
import type { ColumnsType } from "antd/es/table";

import { useGetSortCodeTypeEnumQuery } from "app/configApiSlice";
import { updateLayout } from "pages/Reprocess/reprocessSlice";
import { TransactionType, TransactionTypeEnum, TransactionUpdateDTO } from "models";
import { ApiResponse, SortCodeEnum, TransactionDTO, NIBSSStatusEnum } from "models";
import { useUpdateBatchSingeTransactionMutation } from "pages/Reprocess/reprocessApiSlice";
import { useGetTransactionsQuery } from "pages/TPlusOneProcessing/tPlusOneProcessingApiSlice";

const { Search } = Input;
const { Option } = Select;

const ReprocessEditableTableContainer = styled.div`
  .editable-row .ant-form-item-explain {
    position: absolute;
    top: 100%;
    font-size: 12px;
  }

  .ant-table-content {
    border-radius: 4px;
  }

  colgroup {
    border-radius: 4px;
    col {
      border-radius: 4px;
    }
  }
`;

const ReprocessEditableTable: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams<{ batchId: string }>();
  const dispatch = useAppDispatch();
  const [editingID, setEditingID] = useState(-1);

  const { transactionType } = useOutletContext<{
    sortCode: ApiResponse<SortCodeEnum[]>;
    NIBSSStatus: ApiResponse<NIBSSStatusEnum[]>;
    transactionType: ApiResponse<TransactionTypeEnum[]>;
  }>();

  const sortCode = useGetSortCodeTypeEnumQuery();
  const [reprocessEditableParams, setReprocessEditableParams] = useQueryParams({
    page: withDefault(NumberParam, 0),
    size: withDefault(NumberParam, 10),
    crDr: withDefault(StringParam, "DR"),
    merchant: withDefault(StringParam, ""),
    sortCode: withDefault(StringParam, ""),
    batchId: withDefault(StringParam, params.batchId),
  });

  const transactions = useGetTransactionsQuery(reprocessEditableParams as any);
  const [updateSingleTransaction, updateSingleTransactionResult] =
    useUpdateBatchSingeTransactionMutation();

  const [formData, setFormData] = useState<TransactionDTO[]>(
    transactions.data?.response.content || [],
  );

  const sortCodeOptions = sortCode?.data?.response?.map
    ? sortCode?.data?.response?.map(({ code, bankName }) => ({
        value: code,
        label: bankName,
      }))
    : [];

  const cancel = () => setEditingID(-1);

  const isEditing = (record: TransactionDTO) => {
    return record.id === editingID;
  };

  const edit = (record: Partial<TransactionDTO> | any) => {
    form.setFieldsValue({
      sortCode: "",
      accountName: "",
      accountNumber: "",
      ...record,
    });

    setEditingID(record.id);
  };

  const save = async (key: React.Key | string | any) => {
    try {
      const row = (await form.validateFields()) as TransactionDTO;

      const newData = [...formData];
      const index = newData.findIndex((item) => key === item.id);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });

        setEditingID(-1);
        setFormData(newData);
        handleUpdateSingleTransaction({ ...item, ...row });
      } else {
        setEditingID(-1);
        newData.push(row);
        setFormData(newData);
      }
    } catch (errInfo) {}
  };

  const handleTableChange = (
    pagination: { current?: number; pageSize?: number },
    _filters: unknown,
    _sorter: unknown,
  ) => {
    setReprocessEditableParams({
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  const handleFilterChange = (
    name: string = "",
    value: {
      value?: string;
    },
  ) => {
    setReprocessEditableParams({ [name]: value.value });
  };

  const handleUpdateSingleTransaction = ({
    id,
    batchID,
    sortCode,
    accountName,
    serialNumber,
    accountNumber,
  }: TransactionDTO) => {
    const updateTransactionObj: TransactionUpdateDTO = {
      id,
      batchID,
      sortCode,
      accountName,
      serialNumber,
      accountNumber,
      reprocessUpdate: true,
    };

    updateSingleTransaction(updateTransactionObj)
      .unwrap()
      .then((payload) => {
        transactions.refetch();
        openNotificationWithIcon(``, payload.response, "success");
      })
      .catch((error) => {
        if (error.status >= 500 && error.status < 600) {
          openNotificationWithIcon(error.status, error.data, "error");
        } else if (error.status === 400) {
          error.data?.fieldErrorList?.length
            ? error.data?.fieldErrorList?.forEach((errorMessageObj: any) =>
                openNotificationWithIcon("unsuccessful", errorMessageObj.errorMessage, "warning"),
              )
            : openNotificationWithIcon(error.data.errorMessage, "", "warning");
        }
      });
  };

  useEffect(() => {
    dispatch(
      updateLayout({
        path: "",
        function: "EDIT",
        title: `Re-Process - Edit Transactions - ${params.batchId}`,
      }),
    );
  }, [dispatch, params.batchId]);

  useEffect(() => {
    if (transactions.data?.response.content) {
      setFormData(transactions.data?.response.content);
    }
  }, [transactions.data?.response.content]);

  useEffect(() => {
    if (transactions.isError) {
      const error = transactions?.error as { status: number; data: { error?: string; message?: string; errorMessage?: string; errorMessageList?: string[] } };
      if (error.status >= 500 && error.status < 600) {
        openNotificationWithIcon(error.data.error, error.data.message, "error");
      } else if (error.status === 400) {
        error.data?.errorMessageList?.length
          ? error.data?.errorMessageList?.forEach((errorMessage: string) =>
              openNotificationWithIcon(error.data.errorMessage, errorMessage, "warning"),
            )
          : openNotificationWithIcon(error.data.errorMessage, "", "warning");
      }
    }
  }, [transactions.isError, transactions.error]);

  const columns = [
    {
      title: "Merchant ID",
      dataIndex: "merchant",
      key: "merchant",
      width: "150px",
    },

    {
      title: "CR-DR",
      dataIndex: "crDr",
      key: "crDr",
      width: "80px",
      align: "center",
      render: (value: TransactionType) => <Role role={value} />,
    },

    {
      title: "Account Name",
      dataIndex: "accountName",
      key: "accountName",
      width: "200px",
      editable: true,
      // fixed: "left",

      render: (_: unknown, record: TransactionDTO) => (
        <Typography.Text
          style={{
            width: 200,
            fontSize: "14px",
          }}
          ellipsis={{
            tooltip: {
              color: "#f4f4f4",
              title: <Typography.Text>{record.accountName}</Typography.Text>,
            },
          }}
        >
          {record.accountName}
        </Typography.Text>
      ),
    },

    {
      title: "Account Number",
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: "130px",
      editable: true,
    },

    {
      title: "Sort Code",
      dataIndex: "sortCode",
      key: "sortCode",
      width: "100px",
      editable: true,
    },

    {
      title: "Return Reason",
      dataIndex: "returnReason",
      key: "returnReason",
      width: "120px",

      render: (_: unknown, record: TransactionDTO) => (
        <Typography.Text
          style={{
            width: 130,
            fontSize: "14px",
            textTransform: "lowercase",
          }}
          ellipsis={{
            tooltip: {
              color: "#f4f4f4",
              title: <Typography.Text>{record.errorMessage}</Typography.Text>,
            },
          }}
        >
          {record.errorMessage}
        </Typography.Text>
      ),
    },

    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "12%",
      align: "center",
      render: (_: any, record: TransactionDTO) => {
        const editable = isEditing(record);

        return editable ? (
          <span>
            <Typography.Link
              style={{ marginRight: 8, fontSize: 14 }}
              onClick={() => save(record.id)}
            >
              Save
            </Typography.Link>

            <Typography.Link style={{ fontSize: 14 }} onClick={cancel}>
              Cancel
            </Typography.Link>
          </span>
        ) : (
          <Typography.Link
            style={{ fontSize: 14 }}
            disabled={editingID !== -1 || updateSingleTransactionResult.isLoading}
            onClick={() => edit(record)}
          >
            {(editingID !== -1 || updateSingleTransactionResult.isLoading) &&
            form.getFieldValue("id") === record.id ? (
              <Spin size="small" />
            ) : (
              "Edit"
            )}
          </Typography.Link>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record: TransactionDTO) => ({
        record,
        title: col.title,
        dataIndex: col.dataIndex,
        editing: isEditing(record),
        inputType: col.dataIndex === "age" ? "number" : "text",
      }),
    };
  });

  return (
    <ReprocessEditableTableContainer>
      <Helmet>
        <title>NEFT Settlement | Edit-Transaction</title>
        <meta name="description" content="Helmet application" />
      </Helmet>
      <Row justify="space-between" style={{ padding: "15px 0px" }}>
        <Col>
          <Row gutter={10}>
            <Col>
              <Button
                size="large"
                shape="circle"
                onClick={() => navigate(-1)}
                icon={<ArrowLeftOutlined />}
              />
            </Col>

            <Col>
              <Select
                disabled
                size="large"
                suffixIcon="CR-DR"
                labelInValue={true}
                defaultValue={{ value: "DR" }}
                onChange={(value: {
                  key?: string;
                  label?: string;
                  value?: string;
                  disabled?: boolean;
                }) => handleFilterChange("crDr", value)}
                style={{ width: 140 }}
              >
                <Option value="">All</Option>
                {transactionType &&
                  transactionType.response.map(({ code, description }) => (
                    <Option value={code}>{description}</Option>
                  ))}
              </Select>
            </Col>

            <Col>
              <Select
                showSearch
                size="large"
                labelInValue={true}
                suffixIcon="SORTCODE"
                style={{ width: 250 }}
                optionFilterProp="children"
                defaultValue={{ value: "" }}
                options={[{ value: "", label: " All", key: "all" }, ...sortCodeOptions]}
                filterOption={(input, option) => {
                  return (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase());
                }}
                filterSort={(optionA, optionB) => {
                  return (optionA?.label?.toLowerCase() ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label?.toLowerCase() ?? "").toLowerCase());
                }}
                onChange={(value: {
                  key?: string;
                  label?: string;
                  value?: string;
                  disabled?: boolean;
                }) => handleFilterChange("sortCode", value)}
              />
            </Col>
          </Row>
        </Col>

        <Col>
          <Search
            allowClear
            enterButton
            size="large"
            defaultValue=""
            style={{ width: "auto" }}
            addonBefore="MerchantID"
            placeholder="Enter Merchant ID"
            onSearch={(value: string) => handleFilterChange("merchant", { value })}
          />
        </Col>
      </Row>

      <Form form={form} component={false}>
        <ConfigProvider
          renderEmpty={() =>
            transactions.isError ? (
              <ApiErrorTableComponent queryResult={transactions} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )
          }
        >
          <Table
            key="uuid"
            dataSource={transactions.isError ? [] : formData}
            scroll={{ x: "auto" }}
            rowClassName="editable-row"
            onChange={handleTableChange}
            loading={transactions.isFetching}
            rowKey={(record: TransactionDTO) => record?.id || ""}
            columns={mergedColumns as ColumnsType<TransactionDTO>}
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            pagination={{
              showTitle: true,
              showSizeChanger: true,
              showQuickJumper: true,
              showPrevNextJumpers: true,
              pageSizeOptions: [5, 10, 20, 50, 100, 200],
              pageSize: transactions.data?.response.size,
              total: transactions.data?.response.totalElements,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </ConfigProvider>
      </Form>
    </ReprocessEditableTableContainer>
  );
};

export default ReprocessEditableTable;
